import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlloyButton,
  InlineLoader,
  Modal,
  useModal
} from 'cat-ecommerce-alloy';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import cx from 'classnames';
import EditOrderInformationForm from './EditOrderInformationForm';
import OrderInformationRequestByDate from './OrderInformationRequestByDate';
import { getDealerCustomerStores } from '@app/store/dealer-customer/actions';
import EquipmentDropdown from '@app/components/common/EquipmentDropdown';
import { useWebStorage } from '@app/hooks';
import { getEquipmentField } from '@app/utils/commonUtils';
import useAllEquipment from '@app/hooks/useAllEquipment';
import {
  USER_TYPE_GUEST,
  STATUS,
  AFFILIATION_DEALER,
  EQUIPMENT_VALUE,
  SESSION
} from '@app/constants/commonConstants';
import {
  ERROR_DOMAIN,
  ERROR_PATH,
  FEEDBACK_TYPES
} from '@app/constants/errorConstants';
import Conditional from '@app/components/common/Conditional';
import shoppingStyles from '../styles.module.scss';
import styles from './styles.module.scss';
import { EDIT_ORDER_INFORMATION_MODAL_ID } from './constants';
import { getDefaultRequestByDate } from '../../utils';
import { REQUEST_BY_DATE } from '../../PickUpAndDelivery/constants';

const OrderInformation = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const formikProps = useFormikContext();

  const isEquipmentTouched = formikProps.touched.equipment;
  const equipmentValue = formikProps.values.equipment;

  const customerName = useSelector(s => s.common.selectedCustomerName);
  const customerNumber = useSelector(s => s.common.selectedCustomerNumber);
  const storeLocationName = useSelector(s => s.common.storeLocationName);
  const selectedStore = useSelector(s => s.common.selectedStore);
  const userType = useSelector(state => state.common.userType);
  const isLoggedIn = !!userType && userType !== USER_TYPE_GUEST;
  const displayQuickOrder = useSelector(
    s => s.shoppingPreferences.shoppingPreferencesData.displayQuickOrder
  );
  const userAffiliation = useSelector(s => s.common.userAffiliation);
  const isDealerUser = userAffiliation === AFFILIATION_DEALER;
  const showQuickOrder = displayQuickOrder || isDealerUser;
  const isGuest = !!userType && userType === USER_TYPE_GUEST;
  const requestByDateEnabled = useSelector(
    state => !!state.orders.requestByDate?.requestByDateEnabled
  );
  const currentOrderId = useSelector(s => s.orders.current);
  const currentOrder = useSelector(s => s.orders.byId[currentOrderId] || {});
  const { orderExtendAttribute = {} } = currentOrder;
  const defaultEquipment = useSelector(
    s => s.orders.byId[currentOrderId]?.equipment
  );
  const equipmentList = useSelector(
    s => s.myEquipment?.equipments?.myEquipmentList || []
  );
  const isEquipmentRequired = useSelector(
    s => s.checkout.requiredFields?.equipment
  );
  const ascendingOrderItems = useSelector(
    s => s.orders.byId[currentOrderId]?.ascendingOrderItems
  );
  const hasItemsInCart = !!ascendingOrderItems?.length;
  const endUseCodeLabel = useSelector(s => s.common.selectedEndUseCode?.label);
  const selectedOrderType = useSelector(s => s.common.selectedOrderType);
  const customerNumberStoreInfo = useSelector(
    s => s.dealerCustomer.byId?.[customerNumber]?.store
  );
  const getDealerCustomerStoresStatus = useSelector(
    s => s.dealerCustomer.getDealerCustomerStoresStatus
  );
  const selectedStoreInfo = customerNumberStoreInfo?.find(
    store => store.value === selectedStore
  );
  const isOrderPriceAvailabilityLoading = useSelector(
    s => s.orders.isOrderPriceAvailabilityLoading
  );
  const requestByDate = useSelector(s => s.orders?.requestByDate);
  const defaultEquipmentId = equipmentList?.find(
    e =>
      e.serialNumber === defaultEquipment?.serialNumber ||
      (e.assetId === defaultEquipment?.assetId &&
        e.model === defaultEquipment?.model)
  );
  const [savedEquipmentValue, setSavedEquipmentValue] = useWebStorage(
    EQUIPMENT_VALUE,
    SESSION
  );
  const hasMultipleOrderTypes =
    Array.isArray(selectedStoreInfo?.orderType) &&
    selectedStoreInfo?.orderType?.length > 1;

  const getDealerCustomerStoresLoading =
    getDealerCustomerStoresStatus === STATUS.IDLE ||
    getDealerCustomerStoresStatus === STATUS.PENDING;
  const { getCurrentOrderStatus } = useSelector(s => s.orders);

  const getCurrentOrderLoading =
    getCurrentOrderStatus === STATUS.IDLE ||
    getCurrentOrderStatus === STATUS.PENDING;
  const customerType = useSelector(
    s => s.shoppingPreferences?.shoppingPreferencesData?.customerType
  );
  const isIAUser = customerType && customerType === 'IA';
  const pccDeliveryDropboxRBDByAvailability = useSelector(
    s => s.featureFlag?.PCCDeliveryDropboxRBDByAvailability
  );
  const { isRequiredFieldsLoading } = useSelector(s => s.checkout);
  const dcnData = useSelector(s => s.dealerCustomer.byId);

  useEffect(() => {
    const errorPath = {
      domain: ERROR_DOMAIN.DEALER_CUSTOMER_DCN,
      path: ERROR_PATH.MAIN
    };
    const errorMessageInfo = {
      severity: FEEDBACK_TYPES.WARNING,
      title: '',
      message: t('UNABLE_TO_DISPLAY_ORDER_TYPE')
    };
    !isGuest &&
      dispatch(getDealerCustomerStores({ errorPath, errorMessageInfo }));
  }, [dispatch, t]);
  const { data = {}, loading: isEquipmentLoading } = useAllEquipment();
  const { myEquipmentListData = [] } = data;
  const accountName = orderExtendAttribute?.accountNumber
    ? orderExtendAttribute?.accountNumber
    : `${customerNumber}-${customerName}`;

  const dealerStore = orderExtendAttribute?.dealerStore
    ? orderExtendAttribute?.dealerStore
    : storeLocationName;

  const orderTypeLabel = selectedOrderType?.label || selectedOrderType?.value;

  const orderInformationTable = [
    {
      title: t('FOOTER_ACCOUNT'),
      value: accountName,
      showElement: !!accountName && !isIAUser,
      showLoading: !accountName.includes(customerNumber)
    },
    {
      title: t('MA_OH_STORE_LOC'),
      value: dealerStore,
      showElement: !!dealerStore
    },
    {
      title: t('CAT_SHOPPING_PREFERENCES_END_USE_CODE'),
      value: endUseCodeLabel,
      showElement: !!endUseCodeLabel
    },
    {
      title: t('CAT_SHOPPING_PREFERENCES_ORDER_TYPE'),
      value: orderTypeLabel,
      showElement: !!orderTypeLabel && hasMultipleOrderTypes,
      showLoading: getDealerCustomerStoresLoading
    }
  ];

  const orderInformationValues = orderInformationTable.map(
    ({ title, value, showElement, showLoading }) => {
      if (!showElement && !showLoading) {
        return null;
      }

      return !showLoading ? (
        <div key={`${title}-${value}`} className="d-print-flex mb-2">
          <strong className="u-fs--14 mb-0 u-color--DarkGray text-capitalize text-nowrap">
            {title}
            <span className={shoppingStyles['shopping-cart-page__colon']}>
              :
            </span>
          </strong>
          <p
            className={`u-fs--14 fw-normal mb-0 ${styles['order-information__content--print']} u-color--DarkGray`}
          >
            {value}
          </p>
        </div>
      ) : (
        <div key={`${title}-${value}`} className="py-2 my-1">
          <InlineLoader />
        </div>
      );
    }
  );

  const title = t('SHP_CART_HEADER_ORDER_INFO');

  const editOrderInfoModalProps = useModal();

  const isFetchingInformation =
    isOrderPriceAvailabilityLoading ||
    isRequiredFieldsLoading ||
    getCurrentOrderLoading;

  const storeId = useSelector(s => s.common.storeId);

  const dealerAssociations =
    useSelector(s => s.dealerAssociation.dealerAssociations) || [];

  const customerNumbers = dealerAssociations.find(
    da => da.value === storeId
  )?.customerNumber;
  const storeLocations = dcnData?.[customerNumber]?.store;
  const endUseCodes = storeLocations?.find(
    sl => sl.value === selectedStore
  )?.endUseCode;

  const hasMultipleStores =
    Array.isArray(storeLocations) && storeLocations?.length > 1;
  const hasMultipleAccounts =
    Array.isArray(customerNumbers) && customerNumbers?.length > 1;
  const hasMultipleEndUseCodes =
    Array.isArray(endUseCodes) && endUseCodes.length > 1;

  const showChangeOrderInfo =
    hasMultipleAccounts ||
    hasMultipleStores ||
    hasMultipleEndUseCodes ||
    hasMultipleOrderTypes;

  const showRequestByDate =
    requestByDateEnabled &&
    ((isLoggedIn && !!currentOrderId) || (isGuest && hasItemsInCart));

  const getDefaultEquipment = () => {
    const shouldFindEquipment =
      savedEquipmentValue?.includes(' - ') &&
      savedEquipmentValue?.includes(')');
    const serialNumber = shouldFindEquipment
      ? savedEquipmentValue.split(' - ')[1].split(')')[0]
      : '';
    const objEquipment = equipmentList?.find(
      e => e.serialNumber === serialNumber
    );
    if (savedEquipmentValue && !isEquipmentTouched && !equipmentValue) {
      return shouldFindEquipment ? objEquipment : savedEquipmentValue;
    } else {
      const shouldReturnDefaultEquipment =
        (defaultEquipmentId || defaultEquipment) &&
        !equipmentValue &&
        !isEquipmentTouched;
      const defaultEquipmentValue = defaultEquipmentId || defaultEquipment;
      return shouldReturnDefaultEquipment ? defaultEquipmentValue : '';
    }
  };
  return (
    <div
      className={cx(`col col-lg-12 ${styles['col-print-6']} p-0`, {
        'd-print-none': !isLoggedIn && !formikProps.values.requestByDate
      })}
    >
      <div
        className={cx(`${styles['order-information__container--print']} mb-3`, {
          [styles['order-information__container-quick-order--print']]: !(
            showQuickOrder || isGuest
          )
        })}
      >
        {(isLoggedIn || (isGuest && hasItemsInCart)) && (
          <h2 className="h3 u-fs-md--20 mb-3 u-color--ExtraDarkGray">
            {title}
          </h2>
        )}
        {isLoggedIn && (
          <>
            {orderInformationValues}
            {showChangeOrderInfo && (
              <AlloyButton
                buttonType="ghost"
                onClick={editOrderInfoModalProps.open}
                disabled={isFetchingInformation}
                className="d-print-none po-attachment-delete mt-3 px-0"
              >
                {t('SINGLE_SHIPMENT_PAY_CHANGE')}
              </AlloyButton>
            )}
          </>
        )}
      </div>
      <Modal
        id={EDIT_ORDER_INFORMATION_MODAL_ID}
        close={editOrderInfoModalProps.close}
        isOpen={editOrderInfoModalProps.isOpen}
        title={title}
      >
        <EditOrderInformationForm closeModal={editOrderInfoModalProps.close} />
      </Modal>
      {isLoggedIn && (
        <Conditional
          test={!isFetchingInformation && !isEquipmentLoading}
          fallback={
            <div className="pb-3">
              <InlineLoader />
            </div>
          }
        >
          <EquipmentDropdown
            defaultEquipment={getDefaultEquipment()}
            equipmentField={getEquipmentField(formikProps)}
            helperText={t('EQUIPMENT_HELP1')}
            setSavedEquipmentValue={setSavedEquipmentValue}
            isEquipmentRequired={isEquipmentRequired}
            showOptional={!isEquipmentRequired}
            myEquipmentData={myEquipmentListData}
          />
        </Conditional>
      )}
      {!pccDeliveryDropboxRBDByAvailability && (
        <Conditional
          test={!isFetchingInformation}
          fallback={
            <div className="py-3 mb-4" data-testid="requestByDateInlineloader">
              <InlineLoader />
            </div>
          }
        >
          {showRequestByDate && (
            <OrderInformationRequestByDate requestByDate={requestByDate} />
          )}
        </Conditional>
      )}
    </div>
  );
};

export default OrderInformation;
